// Browser-reported device details for the context add-on
// (PLAN-Device-Context.md §B4.3). Only collected when the link has
// captureContext. Every field is best-effort: whatever a platform doesn't
// expose (model on iOS, memory on Safari…) is simply left out, never guessed.

export interface DeviceReport {
  model?: string;
  platform?: string;
  platformVersion?: string;
  architecture?: string;
  bitness?: string;
  mobile?: boolean;
  fullVersionList?: { brand: string; version: string }[];
  gpu?: string;
  gpuVendor?: string;
  cpuCores?: number;
  memoryGb?: number;
  touchPoints?: number;
  pointer?: 'fine' | 'coarse' | 'none';
  screen?: { w: number; h: number; dpr: number; colorDepth: number };
  timezone?: string;
  languages?: string[];
  webdriver?: boolean;
  networkType?: string;
  effectiveType?: string;
  inAppBrowser?: string;
  clientTimeMs?: number;
}

interface UADataLike {
  platform?: string;
  mobile?: boolean;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}

type NavigatorExtras = Navigator & {
  userAgentData?: UADataLike;
  deviceMemory?: number;
  connection?: { type?: string; effectiveType?: string };
};

const IN_APP_BROWSERS: [RegExp, string][] = [
  [/FBAN|FBAV|FB_IAB/, 'facebook'],
  [/Instagram/, 'instagram'],
  [/WhatsApp/, 'whatsapp'],
  [/TikTok|BytedanceWebview|musical_ly/, 'tiktok'],
  [/LinkedInApp/, 'linkedin'],
  [/Snapchat/, 'snapchat'],
  [/Line\//, 'line'],
  [/Twitter/, 'twitter'],
  [/; wv\)/, 'android-webview'],
];

function inAppBrowser(ua: string): string | undefined {
  return IN_APP_BROWSERS.find(([re]) => re.test(ua))?.[1];
}

function gpuInfo(): { gpu?: string; gpuVendor?: string } {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return {};
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const info = ext
      ? { gpu: gl.getParameter(ext.UNMASKED_RENDERER_WEBGL), gpuVendor: gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) }
      : { gpu: gl.getParameter(gl.RENDERER), gpuVendor: gl.getParameter(gl.VENDOR) };
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return {
      gpu: typeof info.gpu === 'string' ? info.gpu : undefined,
      gpuVendor: typeof info.gpuVendor === 'string' ? info.gpuVendor : undefined,
    };
  } catch {
    return {};
  }
}

function pointerType(): DeviceReport['pointer'] {
  try {
    if (matchMedia('(pointer: coarse)').matches) return 'coarse';
    if (matchMedia('(pointer: fine)').matches) return 'fine';
    return 'none';
  } catch {
    return undefined;
  }
}

async function clientHints(nav: NavigatorExtras): Promise<Partial<DeviceReport>> {
  const ua = nav.userAgentData;
  if (!ua) return {};
  const base: Partial<DeviceReport> = { platform: ua.platform || undefined, mobile: ua.mobile };
  if (!ua.getHighEntropyValues) return base;
  try {
    const hints = await Promise.race([
      ua.getHighEntropyValues(['model', 'platformVersion', 'architecture', 'bitness', 'fullVersionList']),
      new Promise<Record<string, unknown>>((resolve) => setTimeout(() => resolve({}), 300)),
    ]);
    const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
    return {
      ...base,
      model: str(hints.model), // Android only; empty string elsewhere
      platformVersion: str(hints.platformVersion),
      architecture: str(hints.architecture),
      bitness: str(hints.bitness),
      fullVersionList: Array.isArray(hints.fullVersionList)
        ? (hints.fullVersionList as { brand: string; version: string }[]).slice(0, 8)
        : undefined,
    };
  } catch {
    return base;
  }
}

export async function collectDevice(): Promise<DeviceReport> {
  const nav = navigator as NavigatorExtras;
  const report: DeviceReport = {};
  try {
    report.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // leave it out
  }
  report.languages = Array.from(nav.languages || []).slice(0, 8);
  report.cpuCores = nav.hardwareConcurrency || undefined;
  report.memoryGb = nav.deviceMemory;
  report.touchPoints = nav.maxTouchPoints;
  report.pointer = pointerType();
  report.screen = {
    w: window.screen.width,
    h: window.screen.height,
    dpr: window.devicePixelRatio,
    colorDepth: window.screen.colorDepth,
  };
  report.webdriver = nav.webdriver === true;
  report.networkType = nav.connection?.type;
  report.effectiveType = nav.connection?.effectiveType;
  report.inAppBrowser = inAppBrowser(nav.userAgent);
  Object.assign(report, gpuInfo());
  Object.assign(report, await clientHints(nav));
  if (!report.platform && nav.platform) report.platform = nav.platform;
  // Last, so the server's clock-skew computation compares like with like.
  report.clientTimeMs = Date.now();
  return report;
}
